import React, { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@supabase/supabase-js';

// Only initialize Supabase if env vars are configured
const SUPABASE_CONFIGURED = 
  process.env.REACT_APP_SUPABASE_URL && 
  process.env.REACT_APP_SUPABASE_ANON_KEY &&
  process.env.REACT_APP_SUPABASE_URL !== 'undefined';

const supabase = SUPABASE_CONFIGURED 
  ? createClient(process.env.REACT_APP_SUPABASE_URL, process.env.REACT_APP_SUPABASE_ANON_KEY)
  : null;

export { supabase };

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // If Supabase not configured, skip auth and allow app to run freely
    if (!SUPABASE_CONFIGURED || !supabase) {
      setLoading(false);
      return;
    }

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        await fetchProfile(session.user.id);
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchProfile = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (!error && data) {
        setProfile(data);
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email, password, fullName = '') => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName },
      },
    });
    return { data, error };
  };

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { data, error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
  };

  const refreshProfile = async () => {
    if (user) await fetchProfile(user.id);
  };

  // Check if user can create another estimate based on their plan
  const canCreateEstimate = () => {
    if (!profile) return false;
    const plan = profile.plan || 'free';
    if (plan === 'pro' || plan === 'business') return true;
    // Free plan: 1 estimate per month
    const estimateCount = profile.monthly_estimate_count || 0;
    return estimateCount < 1;
  };

  const planLimits = {
    free: { estimates: 1, label: 'Free', price: '$0/mo' },
    pro: { estimates: Infinity, label: 'Pro', price: '$249/mo' },
    business: { estimates: Infinity, label: 'Business', price: '$499/mo' },
  };

  const currentPlan = profile?.plan || 'free';

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        loading,
        supabase,
        signUp,
        signIn,
        signOut,
        refreshProfile,
        canCreateEstimate,
        currentPlan,
        planLimits,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
